import React from 'react';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import NotificationsIcon from '@mui/icons-material/Notifications';
import AccountCircle from '@mui/icons-material/AccountCircle';
import SchoolIcon from '@mui/icons-material/School';
import { TrainingProvider, useTraining } from '../../context/TrainingContext';

const TrainingTopBar = ({ user, onLogout }) => {
  const location = useLocation();
  const { notifications, unreadCount, markNotificationRead } = useTraining();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = React.useState(null);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const isStudent = user?.role?.includes('student');
  const isLecturer = user?.role?.includes('lecturer');
  const isTrainingAdmin = user?.role?.includes('training_admin');
  const isAdmin = user?.role?.includes('admin');
  const isTherapist = user?.role?.includes('therapist');
  // Therapists running a practice session land on /training/session/* via the
  // canonical URL but should never see the lecturer/student chrome — collapse
  // the nav to a single "Back to Simulation Lab" link.
  const isTherapistOnly = isTherapist && !isStudent && !isLecturer && !isTrainingAdmin && !isAdmin;

  const navItems = isTherapistOnly
    ? [{ label: '← Simulation Lab', path: '/therapist/simulation-lab' }]
    : [
      // Student links
      ...(isStudent ? [
        { label: 'Dashboard', path: '/training' },
        { label: 'My Sessions', path: '/training/sessions' },
      ] : []),
      // Scenario library - available to all training roles
      { label: 'Scenario Library', path: '/training/scenarios' },
      // Lecturer links
      ...(isLecturer || isTrainingAdmin || isAdmin ? [
        { label: 'Lecturer Dashboard', path: '/training/lecturer' },
        { label: 'Create Scenario', path: '/training/scenarios/wizard' },
      ] : []),
      // Training admin / platform admin only
      ...(isTrainingAdmin || isAdmin ? [
        { label: 'Training Admin', path: '/training/admin' },
      ] : []),
    ];

  return (
    <AppBar position="sticky" color="default" elevation={1} sx={{ bgcolor: 'background.paper' }}>
      <Toolbar>
        {/* Logo + Training label — switches to "AIMHI Practice" for therapists */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 3 }}>
          <SchoolIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={600} color="primary">
            {isTherapistOnly ? 'AIMHI Practice' : 'AIMHI Training'}
          </Typography>
        </Box>

        {/* Navigation */}
        <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
          {navItems.map(item => (
            <Button
              key={item.path}
              component={RouterLink}
              to={item.path}
              color={isActive(item.path) ? 'primary' : 'inherit'}
              variant={isActive(item.path) ? 'contained' : 'text'}
              size="small"
            >
              {item.label}
            </Button>
          ))}
        </Box>

        {/* Notifications */}
        <IconButton onClick={(e) => setNotifAnchorEl(e.currentTarget)}>
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon />
          </Badge>
        </IconButton>
        <Menu
          anchorEl={notifAnchorEl}
          open={Boolean(notifAnchorEl)}
          onClose={() => setNotifAnchorEl(null)}
          PaperProps={{ sx: { maxWidth: 360, maxHeight: 400 } }}
        >
          {notifications.length === 0 ? (
            <MenuItem disabled>No notifications</MenuItem>
          ) : (
            notifications.slice(0, 10).map(n => (
              <MenuItem
                key={n.id}
                onClick={() => {
                  markNotificationRead(n.id);
                  setNotifAnchorEl(null);
                  if (n.sessionId) navigate(`/training/session/${n.sessionId}/feedback`);
                }}
                sx={{ opacity: n.read ? 0.6 : 1 }}
              >
                <Typography variant="body2" noWrap>{n.message}</Typography>
              </MenuItem>
            ))
          )}
        </Menu>

        {/* User menu */}
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ ml: 1 }}>
          <AccountCircle />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          {user && (
            <MenuItem disabled>
              <Typography variant="body2">{user.name || user.email}</Typography>
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={() => { setAnchorEl(null); onLogout(); }}>
            Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

const TrainingLayoutInner = ({ user, onLogout }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <TrainingTopBar user={user} onLogout={onLogout} />

      {/* Training mode banner */}
      <Alert
        severity="info"
        variant="filled"
        sx={{
          borderRadius: 0,
          justifyContent: 'center',
          py: 0.25,
          '& .MuiAlert-message': { textAlign: 'center' },
        }}
      >
        This is a training exercise with simulated patients. This is not a real clinical encounter.
      </Alert>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          p: { xs: 1, sm: 2, md: 3 },
          maxWidth: 1400,
          mx: 'auto',
          width: '100%',
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

const TrainingLayout = ({ token, user, onLogout }) => {
  return (
    <TrainingProvider token={token} user={user}>
      <TrainingLayoutInner user={user} onLogout={onLogout} />
    </TrainingProvider>
  );
};

export default TrainingLayout;
